import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DATE_PRESET_LABELS, Leg, LegsListItem, LegsState } from './legsTypes';
import { endOfDay, startOfDay } from 'date-fns';
import { SortingModel } from '@/state/general/generalTypes';

const initialState: LegsState = {
    legsList: {},
    latestLegs: {},
    symbolFilter: [],
    legsAnalytics: {},
    legsSorting: {},
    dateSelection: {
        startDate: startOfDay(new Date('01/06/2016')).toISOString(),
        endDate: endOfDay(new Date('01/06/2016')).toISOString(),
        label: DATE_PRESET_LABELS.LAST_50_TRADES,
    },
};

const slice = createSlice({
    name: 'legs',
    initialState,
    reducers: {
        setLegsById: (state, action: PayloadAction<{ accountId: number; legs: LegsListItem }>) => {
            state.legsList[action.payload.accountId] = action.payload.legs;
        },
        addToLegsListById: (
            state,
            action: PayloadAction<{ accountId: number; legs: LegsListItem }>,
        ) => {
            state.legsList[action.payload.accountId].items = [
                ...state.legsList[action.payload.accountId].items,
                ...action.payload.legs.items,
            ];
        },
        setLegsSortingById: (
            state,
            action: PayloadAction<{ accountId: number; sorting: SortingModel }>,
        ) => {
            state.legsSorting[action.payload.accountId] = action.payload.sorting;
        },
        setLastedLegsById: (
            state,
            action: PayloadAction<{ accountId: number; legs: LegsListItem }>,
        ) => {
            state.latestLegs[action.payload.accountId] = action.payload.legs;
        },
        updateLeg: (state, action: PayloadAction<Leg>) => {
            const legList = state.legsList?.[action.payload.apiKeyId];
            const latestLegs = state.latestLegs?.[action.payload.apiKeyId];

            if (legList && legList?.items) {
                const updatedItemIndex = state.legsList[action.payload.apiKeyId].items.findIndex(
                    (el) => el.id === action.payload.id,
                );

                if (updatedItemIndex >= 0) {
                    state.legsList[action.payload.apiKeyId].items.splice(
                        updatedItemIndex,
                        1,
                        action.payload,
                    );
                }
            }

            if (latestLegs) {
                const updatedLastedLegsIndex = state.latestLegs[
                    action.payload.apiKeyId
                ].items.findIndex((el) => el.id === action.payload.id);
                if (updatedLastedLegsIndex >= 0) {
                    state.latestLegs[action.payload.apiKeyId].items.splice(
                        updatedLastedLegsIndex,
                        1,
                        action.payload,
                    );
                }
            }
        },
        resetLegs: (state) => {
            state.legsList = {};
            state.symbolFilter = [];
            state.latestLegs = {};
            state.legsSorting = {};
            state.legsAnalytics = {};
            state.dateSelection = {
                startDate: startOfDay(new Date('01/06/2016')).toISOString(),
                endDate: endOfDay(new Date('01/06/2016')).toISOString(),
                label: DATE_PRESET_LABELS.LAST_50_TRADES,
            };
        },
        removeLegsByAccountId: (state, action: PayloadAction<{ accountId: number }>) => {
            const { accountId } = action.payload;
            if (state.legsList?.[accountId]) {
                delete state.legsList[accountId];
            }
        },
        setSymbolsAnalyticsSorting: (
            state,
            action: PayloadAction<{ [key: string]: SortingModel }>,
        ) => {
            state.legsAnalytics = { ...state.legsAnalytics, ...action.payload };
        },
    },
});

export const {
    setLegsById,
    resetLegs,
    setLastedLegsById,
    updateLeg,
    removeLegsByAccountId,
    addToLegsListById,
    setLegsSortingById,
    setSymbolsAnalyticsSorting,
} = slice.actions;

export default slice.reducer;
